import type { Incident } from '../../services/incidentsApi';

export type RepresentativeEvidencePromotePlan = {
  snapshot: boolean;
  clip: boolean;
};

/** Whether cloud already has a playable/uploaded clip for this incident row. */
export function representativeHasCloudClip(incident: Incident | null | undefined): boolean {
  if (!incident) return false;
  const clipStatus = String((incident as any).clip_status || '').trim().toUpperCase();
  if (clipStatus === 'AVAILABLE' || clipStatus === 'READY') return true;
  if (String((incident as any).clip_uri || '').trim()) return true;
  const attachments = (incident as any).evidence?.attachments;
  if (!Array.isArray(attachments)) return false;
  return attachments.some((attachment: any) => {
    const name = String(attachment?.filename || '').trim();
    const type = String(attachment?.type || '').trim().toLowerCase();
    return type === 'clip' || type === 'video' || /\.(mp4|mov|mkv|webm)$/i.test(name);
  });
}

export function representativeHasCloudSnapshot(incident: Incident | null | undefined): boolean {
  if (!incident) return false;
  const snapStatus = String((incident as any).snapshot_status || '').trim().toUpperCase();
  if (snapStatus === 'AVAILABLE' || snapStatus === 'READY') return true;
  if (String((incident as any).snapshot_uri || '').trim()) return true;
  const attachments = (incident as any).evidence?.attachments;
  if (!Array.isArray(attachments)) return false;
  return attachments.some((attachment: any) => {
    const type = String(attachment?.type || '').trim().toLowerCase();
    return type === 'snapshot' || /\.(jpg|jpeg|png)$/i.test(String(attachment?.filename || ''));
  });
}

/** Infer edge-local clip from API fields or snapshot attachment metadata. */
export function representativeClipLikelyOnEdge(incident: Incident | null | undefined): boolean {
  if (!incident) return false;
  if (Boolean((incident as any).clip_available_on_edge)) return true;
  const promo = String((incident as any).clip_promotion_state || '').trim().toUpperCase();
  if (promo && promo !== 'UPLOADED') return true;
  const attachments = (incident as any).evidence?.attachments;
  if (!Array.isArray(attachments)) return false;
  for (const attachment of attachments) {
    const meta = attachment?.metadata;
    const extra = meta && typeof meta === 'object' ? (meta as any).extra : null;
    if (!extra || typeof extra !== 'object') continue;
    if (extra.clip_available_on_edge === true) return true;
    if (String(extra.clip_file || '').trim()) return true;
  }
  return false;
}

/** Auto-pull plan when the reviewer opens Evidence review. */
export function planRepresentativeEvidenceAutoPromote(
  incident: Incident | null | undefined,
): RepresentativeEvidencePromotePlan | null {
  if (!incident?.incident_id) return null;
  const hasCloudClip = representativeHasCloudClip(incident);
  const hasCloudSnapshot = representativeHasCloudSnapshot(incident);
  const clipPending = !hasCloudClip;
  const snapshotPending = !hasCloudSnapshot;
  if (!clipPending && !snapshotPending) return null;
  return {
    snapshot: snapshotPending,
    // Always try clip when missing in cloud — edge returns clip_not_found if absent.
    clip: clipPending,
  };
}
